import React, { useEffect, useRef, useState } from 'react';
import { productPhoto } from '../photos';

// A product photo on its own colour field. `sizes` lets the browser pick a sensible decode size.
export function ProductPhoto({ product, className = '', eager = false, alt = '' }) {
  const p = productPhoto(product);
  return (
    <span className={`photo ${p.generic ? 'generic' : ''} ${className}`} style={{ '--bg': p.bg }}>
      <img src={p.src} alt={alt} loading={eager ? 'eager' : 'lazy'} decoding="async" width="800" height="800" />
    </span>
  );
}

// Fades sections in as they scroll into view (once). Without IntersectionObserver everything is simply visible.
export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    const els = root.querySelectorAll('.reveal:not(.in)');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    root.classList.add('reveal-ready');
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
  return ref;
}

// The running ticker strip. Screen readers get one plain copy of the text; the two moving copies (doubled so the CSS
// loop is seamless) are hidden from them. A button pauses the motion (WCAG 2.2.2), and so do hover and keyboard focus.
export function Ticker({ items, className = '', label }) {
  const [paused, setPaused] = useState(false);
  const row = items.map((t, i) => <span key={i}>{t}<i>✦</i></span>);
  return (
    <section className={`ticker ${paused ? 'paused' : ''} ${className}`} aria-label={label}>
      <p className="sr-only">{items.join(' · ')}</p>
      <div className="ticker-track" aria-hidden="true">{row}{row}</div>
      <button type="button" className="ticker-toggle" aria-pressed={paused} onClick={() => setPaused(!paused)}
        aria-label={paused ? `Play the ${label.toLowerCase()} ticker` : `Pause the ${label.toLowerCase()} ticker`}>
        {paused ? <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 2l7 4-7 4z" /></svg>
          : <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 2h2v8H3zM7 2h2v8H7z" /></svg>}
      </button>
    </section>
  );
}
